import moment from 'moment';
import { Popover } from 'antd';
import { useState } from 'react';
import isBoolean from 'lodash/isBoolean';

import { GanttAccessRightsEnum } from '@thinknet-gmbh/platform-common';
import { DeleteModal, DeleteModalWithCaptcha } from '@thinknet-gmbh/platform-common-ui';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import {
  ITimeStripe,
  IAbsenceData,
  IEmployeeData,
} from 'src/pages/gantt/gantt-calendar/gantt-view/gantt-view.interfaces';
import { useUpdateTimeStripe } from 'src/hooks/time-stripes/update-time-stripe.hook';
import { useAddAbsenceToRecordEntry } from 'src/hooks/gantts-records/add-gantt-record.hook';
import { DATE_FORMAT_KEY } from 'src/pages/gantt/gantt-calendar/gantt-view/gantt-view.constants';
import { useUpdateGanttAbsenceRecord } from 'src/hooks/gantts-records/update-gantt-absence-record.hook';
import { useDeleteTimeStripe } from 'src/hooks/time-stripes/delete-time-stripe.hook';
import {
  GANTT_VIEW_POPOVER_TYPES,
  GANTT_VIEW_POPOVER_MODAL_TYPES,
  GANTT_VIEW_MODALS,
} from './gantt-popover.constants';
import { IGanttPopover } from './gantt-popover.interfaces';
import { DayEntriesInfo } from './components/day-entries-info/';
import { AbsenceGanttInfo } from './components/absence-gantt-info';
import { EmployeeGanttInfo } from './components/employee-gantt-info';
import { TimeStripesGanttInfo } from './components/time-stripes-gantt-info';
import { AbsenceGanttFormModal } from './components/absence-gantt-form-modal';
import { TimeStripeGanttFormModal } from './components/time-stripe-gantt-form-modal';
import { EmulatedEntry } from './gantt-popover.styles';

const GanttPopover = ({
  popoverInfo,
  t,
  onClose,
  ganttControl,
  setConflictModal,
  setEmployeesOrder,
}: IGanttPopover) => {
  const { entryView, type, target } = popoverInfo;
  const actionAllowed = ganttControl.access === GanttAccessRightsEnum.EDIT;
  const [activeModal, setActiveModal] = useState<null | string>();
  const [editTarget, setEditTarget] = useState<any>();
  const [timeStripe, setTimeStripe] = useState<any>();

  const deleteTimeStripe = useDeleteTimeStripe();

  const { updateTimeStripe } = useUpdateTimeStripe({
    onSuccess: ({ updateTimeStripe: conflicts }) => {
      if (conflicts.length) {
        setConflictModal({ conflicts });
      }

      setActiveModal(null);
      onClose();
    },
  });

  const updateGanttAbsenceRecord = useUpdateGanttAbsenceRecord({
    onSuccess: ({ updateRecordAbsence: conflicts }) => {
      if (conflicts.length) {
        setConflictModal({ conflicts });
      }

      setActiveModal(null);
      onClose();
    },
  });

  const addAbsenceToRecordEntry = useAddAbsenceToRecordEntry({ onSuccess: onClose });
  const currentCompanyId = useGetCurrentCompanyId();

  const onSubmitAbsenceModal = (values: any) => {
    const { date, hours, absenceReasonId, details, fullDay } = values;
    const [from, to] = date;
    const basePayload = {
      dateStartString: moment(from).format(DATE_FORMAT_KEY),
      dateEndString: moment(to).format(DATE_FORMAT_KEY),
      absenceReasonId,
      ...(details && { details }),
      ...(hours && { plannedHours: hours }),
      ...(isBoolean(fullDay) && { fullDay }),
    };

    if (!editTarget) {
      if (target) {
        const payload = {
          ...basePayload,
          companyId: currentCompanyId,
          employeeId: target._id,
          ganttId: target.ganttId,
        };

        const addAbsenceToRecordEntryCallback = ({ confirmed = true }: any = {}) =>
          addAbsenceToRecordEntry({ variables: { payload: { ...payload, confirmed } } }).then(
            ({ data: { addAbsenceToRecordEntry: conflicts } }) => {
              if (conflicts.length) {
                setConflictModal({
                  conflicts,
                  onConfirm: () => {
                    addAbsenceToRecordEntryCallback();
                    setConflictModal(false);
                  },
                });
              }
            }
          );

        addAbsenceToRecordEntryCallback({ confirmed: false });
      }
    } else {
      updateGanttAbsenceRecord({
        variables: { payload: { groupId: target.groupId, ...basePayload } },
      });
    }
  };

  const onSubmitTimeStripeModal = (values: any) => {
    updateTimeStripe({
      _id: editTarget.timeStripeId,
      name: values.name,
      plannedHours: values.plannedHours,
    });
  };

  return (
    <>
      {activeModal === GANTT_VIEW_POPOVER_MODAL_TYPES.absenceForm && (
        <AbsenceGanttFormModal
          absence={editTarget}
          onClose={onClose}
          onSubmitAbsenceModal={onSubmitAbsenceModal}
        />
      )}
      {activeModal === GANTT_VIEW_POPOVER_MODAL_TYPES.timeStripeForm && (
        <TimeStripeGanttFormModal
          timeStripe={editTarget}
          onClose={onClose}
          onSubmitTimeStripeModal={onSubmitTimeStripeModal}
        />
      )}
      {activeModal === GANTT_VIEW_MODALS.deleteTimeStripe && (
        <DeleteModal
          title={t('gantt:labels.removeTimeStripe')}
          subTitle={t('gantt:modals.deleteSubtitle')}
          helperText={`"${timeStripe.stripeName}"?`}
          onSubmit={() => {
            setActiveModal(GANTT_VIEW_MODALS.confirmRemoving);
          }}
          translate={t}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          isOpened
        />
      )}
      {activeModal === GANTT_VIEW_MODALS.confirmRemoving && (
        <DeleteModalWithCaptcha
          title={t('gantt:modals.confirmTitle')}
          subTitle={t('gantt:modals.confirmSubtitle')}
          submitBtnText={t('common:buttons.confirmAndDelete')}
          onSubmit={() => {
            if (timeStripe) {
              deleteTimeStripe({ variables: { timeStripeId: timeStripe._id } }).then(() =>
                onClose()
              );
            }
          }}
          translate={t}
          onClose={() => {
            setActiveModal(null);
            onClose();
          }}
          isOpened
        />
      )}
      <Popover
        autoAdjustOverflow
        placement="rightTop"
        visible={!activeModal}
        getPopupContainer={(triggerNode: any) => triggerNode}
        content={
          <>
            {type === GANTT_VIEW_POPOVER_TYPES.employeeInfo && (
              <EmployeeGanttInfo
                t={t}
                onClose={onClose}
                actionAllowed={actionAllowed}
                employee={target as IEmployeeData}
                setEmployeesOrder={setEmployeesOrder}
                openAbsenceModal={() => setActiveModal(GANTT_VIEW_POPOVER_MODAL_TYPES.absenceForm)}
              />
            )}
            {type === GANTT_VIEW_POPOVER_TYPES.timeStripeInfo && (
              <TimeStripesGanttInfo
                t={t}
                actionAllowed={actionAllowed}
                timeStripe={target as ITimeStripe}
                onClose={onClose}
                openEditModal={() => {
                  setEditTarget({
                    name: target.name,
                    timeStripeId: target.source._id,
                    plannedHours: target.plannedHours,
                    groupId: target.groupId,
                  });
                  setActiveModal(GANTT_VIEW_POPOVER_MODAL_TYPES.timeStripeForm);
                }}
                openDeleteModal={() => {
                  setActiveModal(GANTT_VIEW_MODALS.deleteTimeStripe);
                  setTimeStripe(target);
                }}
              />
            )}
            {type === GANTT_VIEW_POPOVER_TYPES.absenceInfo && (
              <AbsenceGanttInfo
                t={t}
                actionAllowed={actionAllowed}
                absence={target as IAbsenceData}
                onClose={onClose}
                openEditModal={() => {
                  setEditTarget({
                    date: [moment(target.startDate), moment(target.endDate)],
                    absenceReasonId: target.source._id,
                    details: target.details,
                    fullDay: target.fullDay,
                    hours: target.plannedEntryHours,
                  });
                  setActiveModal(GANTT_VIEW_POPOVER_MODAL_TYPES.absenceForm);
                }}
              />
            )}
            {type === GANTT_VIEW_POPOVER_TYPES.dayEntriesInfo && (
              <DayEntriesInfo
                onClose={onClose}
                actionAllowed={actionAllowed}
                ganttRecordId={target._id}
                entries={target.entries}
              />
            )}
          </>
        }
        trigger="click"
        overlayClassName="headerMenuPopover"
        onVisibleChange={visible => !visible && onClose()}
      >
        {entryView && <EmulatedEntry {...entryView} />}
      </Popover>
    </>
  );
};

export default GanttPopover;
